import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import { FormControl } from "@angular/forms";
import { Store } from "@ngrx/store";
import {
  ApiVisa,
  CompBranchLocationEntityLinkContainerModel,
  CompBranchLocationEntityLinkService,
  Pagination,
} from "blg-akaun-ts-lib";
import { PermissionStates } from "../../modules/permission/permission-controller/root";
import { UserPermInquirySelectors } from "../../modules/permission/user-permissions-inquiry-controller/selectors";
import { SessionActions } from "../../modules/session/session-controller/actions";
import { SessionSelectors } from "../../modules/session/session-controller/selectors";
import { SessionStates } from "../../modules/session/session-controller/states";
import { DropDownService } from "../../services/drop-down-service";
import { ReplaySubject, Subject } from "rxjs";
import { take, takeUntil } from "rxjs/operators";
import { SubSink } from "subsink2";

@Component({
  selector: "app-select-multi-location-drop-down-stock-report",
  templateUrl: "./select-multi-location-drop-down-stock-report.component.html",
  styleUrls: ["./select-multi-location-drop-down-stock-report.component.scss"],
})
export class SelectMultiLocationDropDownStockReportComponent
  implements OnInit, OnDestroy
{
  @Input() location: FormControl;
  @Input() apiVisa: ApiVisa;
  @Input() selectedGuid: string;
  @Input() label = "Location";
  @Input() options: any;
  @Input() readPermissionDefintion;
  @Input() companyGuid: string = '';

  @Output() locationSelected = new EventEmitter<any>();

  protected subs = new SubSink();

  userPermissionTarget$ = this.permissionStore.select(
    UserPermInquirySelectors.selectUserPermInquiry
  );

  serverSideFiltering = new FormControl();
  filteredOptions$: ReplaySubject<any[]> = new ReplaySubject<any[]>(1);
  selectedRow: any;
  rowData: any = [];
  key = "location";
  allSelected = false;
  filteredCache: any = [];

  // permission related
  hasPermission: boolean = false;
  locTargets: string[] = [];

  /** flags to set the toggle all checkbox state */
  isIndeterminate = false;
  isChecked = false;

  protected _onDestroy = new Subject<void>();

  constructor(
    private dropDownService: DropDownService,
    private readonly store: Store<SessionStates>,
    private readonly permissionStore: Store<PermissionStates>,
    private readonly compBranchLocationEntityLinkService: CompBranchLocationEntityLinkService
  ) {}

  async ngOnInit() {
    // resolve permission
    this.subs.sink = this.userPermissionTarget$.subscribe(
      async (permissionObject) => {
        this.locTargets = []; // reset loc targets

        // evaluate permissions
        const isOwner: boolean = permissionObject.filter((permission) => permission.permDfn === this.readPermissionDefintion.ownerPermission)[0].hasPermission;
        const isAdmin: boolean = permissionObject.filter((permission) => permission.permDfn === this.readPermissionDefintion.adminPermission)[0].hasPermission;
        const readPerm: any = permissionObject.filter((permission) => permission.permDfn === this.readPermissionDefintion.readPermission)[0];
        const hasReadPerm: boolean = readPerm.hasPermission;

        if (isOwner || isAdmin || hasReadPerm) {
          this.hasPermission = true;
        }

        // if the user isnt admin or owner but has a read permission, evaluate it
        if (!isOwner && !isAdmin && hasReadPerm) {
          await this.getLocTargets(readPerm).then((resolve) => this.locTargets = resolve);
          this.hasPermission = this.locTargets.length > 0 ? true : false; // if somehow there are no location targets, set the user permission to false
        }

        if (this.hasPermission) {
          this.getRowData(); // if permitted, load drop down data
        }
      }
    );

    // listen for search field value changes
    this.subs.sink = this.serverSideFiltering.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.filtering();
      });
  }

  async getLocTargets(readPerm): Promise<string[]> {
    // nice little guard clause function
    if (readPerm.target === null || Object.keys(readPerm.target).length === 0) {
      return [];
    } else if (readPerm.target["bl_inv_mst_location"] && readPerm.target["bl_fi_mst_comp"]) {
      return [...readPerm.target["bl_inv_mst_location"], ...await this.getLocationsFromCompanies(readPerm.target["bl_fi_mst_comp"])];
    } else if (readPerm.target["bl_inv_mst_location"]) {
      return readPerm.target["bl_inv_mst_location"];
    } else if (readPerm.target["bl_fi_mst_comp"]) {
      return await this.getLocationsFromCompanies(readPerm.target["bl_fi_mst_comp"]);
    } else {
      return [];
    }
  }

  async getLocationsFromCompanies(companyGuids: string[]): Promise<string[]> {
    // Create an array to store promises
    const promises = companyGuids.map(async (companyGuid: string) => {
      // pagination
      let pagi: Pagination = new Pagination();
      pagi.conditionalCriteria = [
        { columnName: "comp_guid", operator: "=", value: companyGuid },
      ];

      // Convert observable to promise and await
      const response = await this.compBranchLocationEntityLinkService
        .getByCriteria(pagi, this.apiVisa)
        .toPromise();

      if (response) {
        // Extract and return location_guids from the response
        return response.data.reduce( // reducer, just goes through every element in the response data and adds the location guid to the accumulator if its not null
          (accumulator: string[], currentVal: CompBranchLocationEntityLinkContainerModel ) => {
            if (currentVal.bl_fi_mst_comp_branch_location_entity_link.location_guid) {
              return [...accumulator, currentVal.bl_fi_mst_comp_branch_location_entity_link.location_guid.toString()];
            }
            return accumulator;
          },
          [] // initial value of an empty array
        );
      }
      return []; // Ensure a value is returned in all code paths
    });

    // Await all promises to complete
    const results = await Promise.all(promises);

    // Flatten the array of arrays into a single array of location_guids and removes duplicates using set
    return [...new Set(results.flat())];
  }

  ngOnChanges() {
    console.log('ngOnChanges');
    // if (this.isFilterCompany()) {
    //   this.getRowData();
    // }else{
    //   this.getRowData();
    // }
    this.getRowData();
  }
  
  getRowData() {
    if (this.isFilterCompany() && !this.companyGuid) return;
    console.log('this.isFilterCompany', this.isFilterCompany());
    console.log('this.companyGuid', this.companyGuid);

    let formData = {
      limit: null,
      orderBy: "code",
      order: "ASC",
    };

    if (this.companyGuid) {
      Object.assign(formData,
        {
          "filterConditions": [
          {
            "filterColumn": "guid_bl_fi_mst_comp",
            "filterValues": [this.companyGuid],
            "filterOperator": "="
          }]
        }
      )
    }

    this.subs.sink = this.dropDownService
      .get("location", formData, this.apiVisa)
      .subscribe((resolved) => {
        this.rowData = resolved.data;
        if (this.locTargets?.length > 0) {
          this.rowData = this.rowData.filter((record) =>
            this.locTargets.includes(record.guid)
          );
        }
        if (!this.isFilterCompany()) {
          this.store.dispatch(
            SessionActions.selectDropDownRowData({
              key: this.key,
              rowData: this.rowData,
            })
          );
        }
        //fill the drop down
        this.filteredOptions$.next(this.rowData.slice());

        // set default location
        this.subs.sink = this.store
          .select(SessionSelectors.selectPersonalSettings)
          .subscribe({
            next: (resolve: any) => {
              if (resolve?.DEFAULT_LOCATION) {
                if (this.isMultiple()) {
                  this.location.patchValue([resolve?.DEFAULT_LOCATION]);
                } else {
                  this.location.patchValue(resolve?.DEFAULT_LOCATION);
                }
              }
            },
          });
      });
  }

  filtering() {
    if (!this.rowData) {
      return;
    }
    // get the search keyword
    let search = this.serverSideFiltering.value;
    if (!search) {
      this.filteredOptions$.next(this.rowData.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    // filter
    this.filteredOptions$.next(
      this.rowData.filter(
        (option) =>
          (option.code
            ? option.code.toLowerCase().indexOf(search) > -1
            : null) ||
          (option.name ? option.name.toLowerCase().indexOf(search) > -1 : null)
      )
    );
  }

  onSelect(guid: string) {
    if (guid) {
      this.location.patchValue(guid);
      this.selectedRow = this.rowData.find((row) => row.guid === guid);
      this.locationSelected.emit(this.selectedRow);
    }
  }

  isRequired() {
    let value = this.options["required"];
    if (value === true || value === false) {
      return value;
    } else if (value) {
      return value.includes(true);
    }
    return false;
  }

  isMultiple() {
    let value = this.options["multiple"];
    if (value === true || value === false) {
      return value;
    } else if (value) {
      return value.includes(true);
    }
    return false;
  }

  isFilterCompany() {
    let value = this.options['filterCompany'];
    if (value === true || value === false) {
      return value;
    }
    else if (value) {
      return value.includes(true);
    }
    return false; 
  }

  toggleSelectAll(selectAllValue: boolean) {
    this.subs.sink = this.filteredOptions$
      .pipe(take(1), takeUntil(this._onDestroy))
      .subscribe((val) => {
        if (selectAllValue) {
          this.location.patchValue([...val.map((item) => item.guid)]);
          //this.location.patchValue(val);
        } else {
          this.location.patchValue([]);
        }
      });
  }

  setToggleAllCheckboxState() {
    if (!this.isMultiple()) return;
    let filteredLength = 0;
    if (this.location && this.location.value) {
      this.filteredCache.forEach((el) => {
        if (this.location.value.indexOf(el) > -1) {
          filteredLength++;
        }
      });
      this.isIndeterminate =
        filteredLength > 0 && filteredLength < this.filteredCache.length;
      this.isChecked =
        filteredLength > 0 && filteredLength === this.filteredCache.length;
    }
  }

  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
    this.subs.unsubscribe();
  }
}
